// Marks that the current browser arrived via the top-level
// `/photoshoot-handoff` protocol (see `photoshootHandoffProtocol.ts` and
// `docs/PHOTOSHOOT_TOP_LEVEL_HANDOFF.md`). Consumed by yearly-plan trial
// gating in `SignInManager` so photoshoot-handoff sessions never see the
// yearly free trial, regardless of the backend `can_trial` flag.
//
// Backed by `localStorage` (not `sessionStorage`) with a short bounded TTL,
// same shape as `attributionContext.ts`'s UTM persistence. The handoff's main
// flow redirects the *opener* tab and closes the temporary handoff popup
// (see docs) -- `sessionStorage` is scoped per top-level browsing context, so
// a value written in the popup is invisible to the opener tab it hands off
// to. `localStorage` is shared same-origin across tabs immediately, which
// fixes that at the root instead of relying on every redirect hop threading
// a query param through and something reading it before the param is lost.
// The TTL keeps this from lingering indefinitely into unrelated future visits.
const PHOTOSHOOT_HANDOFF_ORIGIN_STORAGE_KEY = 'photoshoot_handoff_origin_v1'
const PHOTOSHOOT_HANDOFF_ORIGIN_TTL_MS = 1000 * 60 * 60 // 1 hour

// Kept as a defense-in-depth signal for the popup -> opener redirect (in case
// a tab lost its localStorage, e.g. Safari ITP edge cases) -- not load-bearing
// on its own since `markPhotoshootHandoffOrigin` already writes to storage
// that's immediately visible from the opener tab.
export const PHOTOSHOOT_HANDOFF_ORIGIN_QUERY_PARAM = 'photoshootHandoffOrigin'

interface StoredPhotoshootHandoffOrigin {
  expiresAt: number
}

function isBrowser() {
  return typeof window !== 'undefined'
}

function readStoredPayload(): StoredPhotoshootHandoffOrigin | null {
  if (!isBrowser()) return null
  try {
    const raw = window.localStorage.getItem(PHOTOSHOOT_HANDOFF_ORIGIN_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredPhotoshootHandoffOrigin
    if (!parsed?.expiresAt || parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(PHOTOSHOOT_HANDOFF_ORIGIN_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function markPhotoshootHandoffOrigin(): void {
  if (!isBrowser()) return
  try {
    const payload: StoredPhotoshootHandoffOrigin = {
      expiresAt: Date.now() + PHOTOSHOOT_HANDOFF_ORIGIN_TTL_MS,
    }
    window.localStorage.setItem(PHOTOSHOOT_HANDOFF_ORIGIN_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // no-op (e.g. private browsing storage restrictions)
  }
}

/**
 * Eagerly captures the handoff-origin marker from a URL query param. Call
 * this once, unconditionally, at known handoff-continuation entry points
 * (`Sso`, `Photoshoots` hydration) -- mirrors `captureAttributionFromSearch`.
 * Reading eagerly at a guaranteed mount point (rather than lazily, whenever
 * some unrelated caller happens to invoke `hasPhotoshootHandoffOrigin`)
 * removes the dependency on incidental call-timing elsewhere in the app.
 */
export function capturePhotoshootHandoffOriginFromSearch(search?: string): void {
  if (!isBrowser()) return
  try {
    const params = new URLSearchParams(search ?? window.location.search)
    if (params.get(PHOTOSHOOT_HANDOFF_ORIGIN_QUERY_PARAM) === '1') {
      markPhotoshootHandoffOrigin()
    }
  } catch {
    // no-op (e.g. malformed location in test harnesses)
  }
}

// Intentionally NOT cleared on logout: this is a device/browser-level
// anti-gaming signal bounded by its own TTL, not a per-session/per-account
// attribute. Clearing it on logout would let a user bypass the yearly-trial
// withholding simply by logging out and signing into a different (or fresh)
// account on the same browser within the TTL window -- exactly the
// "clear session to game the trial" path this gate exists to prevent. It
// only ever goes away via TTL expiry (see `readStoredPayload`).
export function hasPhotoshootHandoffOrigin(): boolean {
  if (readStoredPayload()) return true
  // Defense-in-depth: bootstrap from the query param even if a dedicated
  // entry point never called `capturePhotoshootHandoffOriginFromSearch`
  // (e.g. a future landing route that forwards the param but forgets to).
  capturePhotoshootHandoffOriginFromSearch()
  return readStoredPayload() !== null
}
